import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Divider,
  Badge,
  Menu,
  MenuItem,
  Alert,
} from '@mui/material';
import BotStatusIndicator from '../components/BotStatusIndicator';
import {
  Search,
  Send,
  AttachFile,
  EmojiEmotions,
  WhatsApp,
  Sms,
  Phone,
  MoreVert,
  SmartToy,
  Person,
  TransferWithinAStation,
  CheckCircle,
  Warning,
} from '@mui/icons-material';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface Conversation {
  id: string;
  customer_id: string;
  customer_name: string;
  channel: string;
  status: string;
  handled_by: string;
  message_count: number;
  interest_level: string;
  outcome: string;
  created_at: string;
  last_message_at: string;
  tags: string[];
}

interface Message {
  id: string;
  text: string;
  sender: 'customer' | 'bot' | 'agent';
  timestamp: string;
  status?: 'sent' | 'delivered' | 'read';
  metadata?: {
    intent?: string;
    confidence?: number;
  };
}



const Conversations: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showHandoffDialog, setShowHandoffDialog] = useState(false);

  // Fetch conversations from API
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const response = await fetch(`${API_URL}/v1/conversations/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch conversations');
        }

        const data = await response.json();
        setConversations(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching conversations:', err);
        setError('Failed to load conversations');
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, []);

  // Fetch messages when a conversation is selected
  useEffect(() => {
    const fetchMessages = async () => {
      if (!selectedConversation) {
        setMessages([]);
        return;
      }

      try {
        setLoadingMessages(true);
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const response = await fetch(`${API_URL}/v1/conversations/${selectedConversation.id}/messages`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch messages');
        }

        const messagesData = await response.json();
        setMessages(messagesData);
      } catch (err) {
        console.error('Error fetching messages:', err);
        // If no messages found, show empty array
        setMessages([]);
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchMessages();
  }, [selectedConversation]);

  const getChannelIcon = (channel: 'whatsapp' | 'sms') => {
    return channel === 'whatsapp' ?
      <WhatsApp sx={{ fontSize: 20, color: '#25D366' }} /> :
      <Sms sx={{ fontSize: 20, color: '#2196F3' }} />;
  };


  const handleSendMessage = () => {
    if (!newMessage.trim()) return;

    const message: Message = {
      id: String(messages.length + 1),
      text: newMessage,
      sender: 'agent',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      status: 'sent',
    };

    setMessages([...messages, message]);
    setNewMessage('');
  };

  const handleHandoff = () => {
    setShowHandoffDialog(false);
    // Implement handoff logic
    alert('Conversation handed off to human agent');
  };

  const filteredConversations = conversations.filter(conv =>
    conv.customer_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          WhatsApp & SMS Conversations
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BotStatusIndicator
            status="active"
            confidence={91}
            variant="detailed"
            showMetrics={true}
            lastActivity="1 min ago"
            activeConversations={8}
          />
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ height: 'calc(100vh - 140px)' }}>
        {/* Conversation List */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Filter Chips */}
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label="All" size="small" color="primary" />
                <Chip label="Bot Active" size="small" variant="outlined" />
                <Chip label="Needs Handoff" size="small" variant="outlined" />
                <Chip label="With Agent" size="small" variant="outlined" />
              </Box>
            </Box>

            <Divider />

            <List sx={{ flex: 1, overflow: 'auto' }}>
              {loading ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography>Loading conversations...</Typography>
                </Box>
              ) : error ? (
                <Alert severity="error" sx={{ m: 2 }}>
                  {error}
                </Alert>
              ) : filteredConversations.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No conversations found
                  </Typography>
                </Box>
              ) : (
                filteredConversations.map((conv) => (
                  <ListItem
                    key={conv.id}
                    button
                    selected={selectedConversation?.id === conv.id}
                    onClick={() => setSelectedConversation(conv)}
                    sx={{
                      '&.Mui-selected': {
                        bgcolor: '#E3F2FD',
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: '#1a237e' }}>
                        {conv.customer_name.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" fontWeight="600">
                            {conv.customer_name}
                          </Typography>
                          <Chip
                            label={conv.channel}
                            size="small"
                            sx={{
                              bgcolor: conv.channel === 'simulator' ? '#E1F5FE' : '#E8F5E9',
                              color: conv.channel === 'simulator' ? '#0277BD' : '#2E7D32'
                            }}
                          />
                          {conv.tags.includes('Simulated') && (
                            <Chip
                              label="Simulated"
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                          {conv.handled_by === 'bot' && <SmartToy sx={{ fontSize: 16, color: '#2196F3' }} />}
                          {conv.status === 'handed_off' && <Warning sx={{ fontSize: 16, color: '#FF9800' }} />}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            Status: {conv.status} | Interest: {conv.interest_level} | Messages: {conv.message_count}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                            <Chip
                              label={conv.outcome || 'In Progress'}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.7rem',
                                bgcolor: conv.outcome === 'qualified' ? '#E8F5E9' : '#FFF3E0',
                                color: conv.outcome === 'qualified' ? '#4CAF50' : '#FF9800',
                              }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {new Date(conv.last_message_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>

        {/* Conversation View */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {selectedConversation ? (
              <>
                {/* Header */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: '#1a237e' }}>
                        {selectedConversation.customer_name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="h6">
                          {selectedConversation.customer_name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Channel: {selectedConversation.channel}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Interest: {selectedConversation.interest_level}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Messages: {selectedConversation.message_count}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip
                        label={selectedConversation.status}
                        color={selectedConversation.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                      <Chip
                        label={selectedConversation.outcome || 'In Progress'}
                        color={selectedConversation.outcome === 'qualified' ? 'success' : 'warning'}
                        size="small"
                      />
                      {selectedConversation.tags.includes('Simulated') && (
                        <Chip
                          label="Simulated"
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Messages */}
                <Box sx={{ flex: 1, p: 2, overflow: 'auto', bgcolor: '#FAFAFA' }}>
                  {loadingMessages ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography>Loading messages...</Typography>
                    </Box>
                  ) : messages.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography color="text.secondary">No messages found</Typography>
                    </Box>
                  ) : (
                    messages.map((message) => (
                      <Box
                        key={message.id}
                        sx={{
                          display: 'flex',
                          justifyContent: message.sender === 'customer' ? 'flex-start' : 'flex-end',
                          mb: 2,
                        }}
                      >
                      <Box
                        sx={{
                          maxWidth: '70%',
                          p: 2,
                          borderRadius: 2,
                          bgcolor:
                            message.sender === 'customer'
                              ? 'white'
                              : message.sender === 'bot'
                              ? '#E3F2FD'
                              : '#4CAF50',
                          color:
                            message.sender === 'agent' ? 'white' : 'text.primary',
                          boxShadow: 1,
                        }}
                      >
                        {message.sender !== 'customer' && (
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                            {message.sender === 'bot' ? '🤖 AI Bot' : '👤 Agent'}
                          </Typography>
                        )}
                        <Typography variant="body2">{message.text}</Typography>
                        {message.metadata && (
                          <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              Intent: {message.metadata.intent}
                            </Typography>
                            <Typography variant="caption">
                              Confidence: {message.metadata.confidence}%
                            </Typography>
                          </Box>
                        )}
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                          {message.timestamp}
                          {message.status && (
                            <>
                              {' • '}
                              {message.status === 'read' && <CheckCircle sx={{ fontSize: 12 }} />}
                              {message.status === 'delivered' && '✓✓'}
                              {message.status === 'sent' && '✓'}
                            </>
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                  )}
                </Box>

                {/* Input */}
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'white' }}>
                  <TextField
                    fullWidth
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton>
                            <EmojiEmotions />
                          </IconButton>
                          <IconButton>
                            <AttachFile />
                          </IconButton>
                          <IconButton
                            color="primary"
                            onClick={handleSendMessage}
                            sx={{ bgcolor: '#4CAF50', color: 'white', '&:hover': { bgcolor: '#43A047' } }}
                          >
                            <Send />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {selectedConversation.status === 'bot' && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      AI Bot is currently handling this conversation
                    </Typography>
                  )}
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography color="text.secondary">Select a conversation to view messages</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => setAnchorEl(null)}>View Customer Profile</MenuItem>
        <MenuItem onClick={() => setAnchorEl(null)}>Export Conversation</MenuItem>
        <MenuItem onClick={() => setAnchorEl(null)}>Mark as Qualified</MenuItem>
        <MenuItem onClick={() => setAnchorEl(null)}>Close Conversation</MenuItem>
      </Menu>

      {/* Handoff Dialog */}
      {showHandoffDialog && (
        <Paper
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            p: 3,
            zIndex: 1300,
            minWidth: 400,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Request Agent Handoff
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The AI bot will transfer this conversation to a human agent.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Reason for handoff (optional)"
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowHandoffDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleHandoff} sx={{ bgcolor: '#FF9800' }}>
              Request Handoff
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default Conversations;